/*
 * Copyright (c) Nexign, JSC, 2021-2023.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @author Evgeniy Naumov
 * @version 1.25.1
 * @since 1.15.0
 */

import { Fragment, useCallback, useEffect } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { ELogBusinessEventName, ELogBusinessEventType, logBusinessEvent } from '@modules/logging';
import { orderEventMapping, orderEventType } from '@modules/logging/loggingConstants';
import { FeatureNameEnum } from '@modules/role/constants/guard/guardConstants';
import { makeSelectFeatureEnabled } from '@pages/PageAuth/selectors/authSelectors';

import { Button, InfoBlock, PageTitle, Skeleton } from '../../../components/common';
import BadgeStatus from '../../../components/common/Badge/BadgeStatus';
import Title from '../../../components/common/Title/Title';
import { setHelpTipTextAction } from '../../../components/Header/headerActions';
import { TableFilterPresetQueryId } from '../../../components/Table/types/tableTypes';
import { EColorStatus } from '../../../modules/app/appConstants';
import PageLocale from '../../../modules/internationalization/components/PageLocale';
import { TranslationFileNamesEnum } from '../../../modules/internationalization/constants';
import { ELocaleKeys } from '../../../modules/internationalization/intConstants';
import {
    makeSelectPageOrderIdTranslation,
    makeSelectTranslationCommon,
} from '../../../modules/internationalization/intSelectors';
import { ECommonTranslationPages } from '../../../modules/internationalization/types/common/pages';
import { TableExport } from '../../../modules/table';
import { openRoute } from '../../../routes/constants/externalRoutes';
import { IRouteParams, RoutePath } from '../../../routes/constants/routesConstants';
import { icons as themeIcons } from '../../../theme';
import { formatDate } from '../../../utils/formatText';
import { stringFormatParams } from '../../../utils/stringFormat';
import ErrorPage from '../../ErrorPage/ErrorPage';
import { mapOrderIdStatusToBadgeType } from '../constant';
import { orderIdApi, useGetOrderIdMetaQuery, useSendOrderIdViewedMutation } from '../store';
import { EOrderIdStatus } from '../types';
import OrderIdFileList from './Files/OrderIdFileList';
import OrderIdParameters from './OrderIdParameters';
import { OrderIdSkeleton } from './OrderIdSkeleton';
import { PageOrderIdStyled } from './styled/PageOrderIdStyled';
import OrderIdTable from './Table/OrderIdTable';

const PageOrderId = () => {
    const { orderId } = useParams<IRouteParams>();

    const dispatch = useDispatch();

    const translationCommon = useSelector(makeSelectTranslationCommon());
    const translationOrderId = useSelector(makeSelectPageOrderIdTranslation());
    const isCdmAdditionalReportsEnabled = useSelector(makeSelectFeatureEnabled(FeatureNameEnum.CDM_ADDITIONAL_REPORTS))

    const { data, isFetching, isSuccess, error } = useGetOrderIdMetaQuery(
        { orderId },
        {
            skip: !orderId,
        },
    );

    const [sendIdViewed] = useSendOrderIdViewedMutation();

    useEffect(() => {
        if (!data) return;

        if (data.subsCountWithErrors) {
            dispatch(setHelpTipTextAction(translationOrderId.contextHelp.subsCountWithErrors));
        } else if (!!data.files.files?.length && !data.files.isReportNotAvailable) {
            dispatch(setHelpTipTextAction(translationOrderId.contextHelp.fileLoadSuccess));
        }
    }, [data, translationOrderId.contextHelp, dispatch]);

    useEffect(() => {
        if (!data?.actionId) return;

        dispatch(
            logBusinessEvent({
                eventName:
                    orderEventMapping[orderEventType.b2b][data.actionId] ||
                    ELogBusinessEventName.USER_SERVICES_ORDER_INFO,
                type: ELogBusinessEventType.common,
            }),
        );
    }, [data, dispatch]);

    useEffect(() => {
        return () => {
            dispatch(orderIdApi.util.resetApiState());
        };
    }, [dispatch]);

    useEffect(() => {
        if (orderId && !isFetching && isSuccess && data) {
            sendIdViewed({ orderId });
        }
    }, [data, isFetching, isSuccess, orderId, sendIdViewed]);

    const onShowSubsClick = useCallback(() => {
        openRoute(`${RoutePath.subscriberMobile}?${TableFilterPresetQueryId}=order-id-errors-${orderId}`);
    }, [orderId]);

    return (
        <PageOrderIdStyled>
            <PageLocale lazy fileNames={[TranslationFileNamesEnum.pageOrderId]} />

            <Title pageName={ECommonTranslationPages.pageOrderId} />

            <Skeleton isLoading={isFetching} render={OrderIdSkeleton}>
                {error || (isSuccess && !data) ? (
                    <ErrorPage
                        src={themeIcons.errorDeliveryImage3x}
                        text={translationCommon.error.ui.text}
                        title={translationCommon.error.ui.title}
                    />
                ) : (
                    data && (
                        <Fragment>
                            <PageTitle
                                count={data.count || undefined}
                                title={stringFormatParams(translationOrderId.title, {
                                    [ELocaleKeys.name]: data.name,
                                })}
                            />

                            <div className="order__content">
                                {/* Order Info */}
                                <div className="order__info">
                                    <BadgeStatus
                                        className="order__block order__status"
                                        dataName="order-id-status"
                                        status={mapOrderIdStatusToBadgeType[data.status]}
                                        text={stringFormatParams(translationOrderId.info.statusLabel, {
                                            [ELocaleKeys.status]: translationOrderId.info.status[data.status],
                                        })}
                                    />
                                    <div className="order__block">
                                        <div className="order__block-label order__block-label_light">
                                            {translationOrderId.info.date}
                                        </div>
                                        {formatDate(data.date)}
                                    </div>
                                    <div className="order__block">
                                        <div className="order__block-label order__block-label_light">
                                            {translationOrderId.info.user}
                                        </div>
                                        {data.user}
                                    </div>
                                </div>

                                {/* Subs Errors */}
                                {!!data.subsCountWithErrors && (
                                    <div className="order__subs-errors">
                                        <div className="order__sub-title order__block-label order__block-label_bold">
                                            {stringFormatParams(translationOrderId.subsErrors.title, {
                                                number: data.subsCountWithErrors,
                                            })}
                                        </div>
                                        <div className="order__block">
                                            <Button onClick={onShowSubsClick}>
                                                {translationOrderId.subsErrors.btn}
                                            </Button>
                                        </div>
                                        {data.exports?.length && (
                                            <div className="order__block order__export">
                                                <TableExport
                                                    options={data.exports}
                                                    url={stringFormatParams('order/{orderId}/export', { orderId })}
                                                />
                                            </div>
                                        )}
                                    </div>
                                )}

                                {
                                    /* Files */
                                    !!data.files.files?.length && !data.files.isReportNotAvailable && (
                                        <div className="order__files">
                                            <OrderIdFileList
                                                file={data.files}
                                                isCdmAdditionalReportsEnabled={isCdmAdditionalReportsEnabled}
                                                orderId={orderId}
                                            />
                                        </div>
                                    )
                                }
                                {data.files?.isReportNotAvailable && data.status === EOrderIdStatus.success && (
                                    <div className="order__files">
                                        <InfoBlock className="order__files-error" colorStatus={EColorStatus.ERROR}>
                                            {translationOrderId.file.reportNotAvailable}
                                        </InfoBlock>
                                    </div>
                                )}

                                {/* Parameters */}
                                {!!Object.keys(data.parameters).length && (
                                    <div className="order__parameters">
                                        <OrderIdParameters parameters={data.parameters} />
                                    </div>
                                )}

                                {!!data.isShowSubsList && <OrderIdTable orderId={orderId} />}
                            </div>
                        </Fragment>
                    )
                )}
            </Skeleton>
        </PageOrderIdStyled>
    );
};

export default PageOrderId;
